/** Shared helpers for the Campaign Dashboard. */

import { isWizardInProgress } from "@/lib/campaignWorkflow";

export const CAMPAIGN_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "creation_in_progress", label: "Creation In Progress" },
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
];

/** All campaign filter options — Signal sources only (transport lives in workflow). */
export const CAMPAIGN_SIGNAL_OPTIONS = [
  { value: "all", label: "All signals" },
  { value: "rnc", label: "RNC" },
  { value: "custom", label: "ABM List" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "quora", label: "Quora" },
  { value: "reddit", label: "Reddit" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "google_ads", label: "Google Ads" },
  { value: "website", label: "Website" },
  { value: "zillow", label: "Zillow" },
  { value: "realtor", label: "Realtor.com" },
  { value: "redfin", label: "Redfin" },
  { value: "x", label: "X" },
];

/** @deprecated use CAMPAIGN_SIGNAL_OPTIONS */
export const CAMPAIGN_CHANNEL_OPTIONS = CAMPAIGN_SIGNAL_OPTIONS;

/** Assign-signals modal — Signal catalog only (no transport). */
export const OUTREACH_ASSIGN_SIGNAL_OPTIONS = [
  { key: "rnc", name: "RNC" },
  { key: "custom", name: "ABM List" },
  { key: "linkedin", name: "LinkedIn" },
  { key: "quora", name: "Quora" },
  { key: "reddit", name: "Reddit" },
  { key: "facebook", name: "Facebook" },
  { key: "instagram", name: "Instagram" },
  { key: "google_ads", name: "Google Ads" },
  { key: "website", name: "Website" },
  { key: "zillow", name: "Zillow" },
  { key: "realtor", name: "Realtor.com" },
  { key: "redfin", name: "Redfin" },
  { key: "x", name: "X" },
];

/** @deprecated use OUTREACH_ASSIGN_SIGNAL_OPTIONS */
export const OUTREACH_ASSIGN_CHANNEL_OPTIONS = OUTREACH_ASSIGN_SIGNAL_OPTIONS;

/** Normalize channel keys so filter values match stored campaign channels. */
export function normalizeCampaignChannelKey(key) {
  const k = String(key || "").toLowerCase().trim();
  if (k === "googleads" || k === "google-ads") return "google_ads";
  if (k === "twitter") return "x";
  if (k === "gmail") return "email";
  return k;
}

export const OUTREACH_SENDABLE_CHANNELS = new Set(
  OUTREACH_ASSIGN_CHANNEL_OPTIONS.map((c) => c.key),
);

function _getDefaultSignals(raw, settings) {
  if (Array.isArray(raw.defaultForSignals)) {
    return raw.defaultForSignals;
  }
  if (Array.isArray(raw.default_for_signals)) {
    return raw.default_for_signals;
  }
  if (Array.isArray(raw.defaultForChannels)) {
    return raw.defaultForChannels;
  }
  if (Array.isArray(raw.default_for_channels)) {
    return raw.default_for_channels;
  }
  if (Array.isArray(settings.default_for_signals)) {
    return settings.default_for_signals;
  }
  if (Array.isArray(settings.default_for_channels)) {
    return settings.default_for_channels;
  }
  return [];
}

function _getAssignedSignals(raw, settings) {
  if (Array.isArray(raw.assignedSignals)) {
    return raw.assignedSignals;
  }
  if (Array.isArray(raw.assigned_signals)) {
    return raw.assigned_signals;
  }
  if (Array.isArray(settings.assigned_signals)) {
    return settings.assigned_signals;
  }
  if (Array.isArray(settings.assigned_channels)) {
    return settings.assigned_channels;
  }
  if (Array.isArray(raw.assignedChannels)) {
    return raw.assignedChannels;
  }
  if (Array.isArray(raw.channels)) {
    return raw.channels;
  }
  return [];
}

function _getIsDefault(raw, settings, defaultForSignals) {
  return raw.isDefault === true
    || defaultForSignals.length > 0
    || Boolean(settings.is_default || settings.isDefault);
}

function _getCounts(raw) {
  const runningSignals = Number(
    raw.runningSignalsCount ?? raw.running_signals_count ?? 0,
  ) || 0;
  return {
    runningSignalsCount: runningSignals,
    qualifiedRNCCount: Number(raw.qualifiedRNCCount ?? raw.qualified_rnc_count ?? 0) || 0,
    conversionRate: Number(raw.conversionRate ?? raw.conversion_rate ?? 0) || 0,
  };
}

function _getSignalSources(raw, settings, assignedSignals) {
  if (Array.isArray(raw.signalSources)) {
    return raw.signalSources.map(String);
  }
  if (Array.isArray(settings.signal_sources)) {
    return settings.signal_sources.map(String);
  }
  return assignedSignals.map(String);
}

export function normalizeDashboardCampaign(raw = {}) {
  const id = String(raw.id || raw.campaignId || "");
  const name = String(raw.campaignName || raw.name || "Untitled campaign");
  const workflowName = String(
    raw.workflowName || raw.workflow_name || raw.template || name,
  );
  const settings = raw.settings && typeof raw.settings === "object" ? raw.settings : {};

  const defaultForSignals = _getDefaultSignals(raw, settings);
  const assignedSignals = _getAssignedSignals(raw, settings);
  const updatedAt = raw.updatedAt || raw.updated_at || null;
  const isDefault = _getIsDefault(raw, settings, defaultForSignals);
  const counts = _getCounts(raw);
  const signalSources = _getSignalSources(raw, settings, assignedSignals);

  return {
    id,
    campaignId: id,
    campaignName: name,
    name,
    workflowName,
    assignedSignals,
    assignedChannels: assignedSignals,
    channels: assignedSignals,
    signalSources,
    defaultForSignals,
    defaultForChannels: defaultForSignals,
    status: String(raw.status || "draft").toLowerCase(),
    runningSignalsCount: counts.runningSignalsCount,
    qualifiedRNCCount: counts.qualifiedRNCCount,
    conversionRate: counts.conversionRate,
    updatedAt,
    lastActivity: raw.last_activity || null,
    description: raw.description || "",
    isDefault,
    settings,
  };
}

function _campaignSignalKeys(c) {
  const keys = [
    ...(c.assignedSignals || c.assignedChannels || c.channels || []),
    ...(c.signalSources || []),
  ];
  return new Set(keys.map(normalizeCampaignChannelKey).filter(Boolean));
}

function _campaignMatchesStatus(c, status) {
  if (status === "all") return true;
  const inProgress = isWizardInProgress(c);
  if (status === "creation_in_progress") return inProgress;
  if (status === "draft") return c.status === "draft" && !inProgress;
  return c.status === status;
}

function _campaignMatchesFilters(c, q, status, channel) {
  if (q && !String(c.campaignName || c.name || "").toLowerCase().includes(q)) return false;
  if (!_campaignMatchesStatus(c, status)) return false;
  if (channel !== "all") {
    const wanted = normalizeCampaignChannelKey(channel);
    if (!_campaignSignalKeys(c).has(wanted)) return false;
  }
  return true;
}

export function filterDashboardCampaigns(campaigns, {
  search = "",
  status = "all",
  channel = "all",
} = {}) {
  const q = String(search || "").trim().toLowerCase();
  return (campaigns || []).filter((c) => _campaignMatchesFilters(c, q, status, channel));
}

/** Default campaign first, then newest updated (stable for missing timestamps). */
export function sortCampaignsByUpdatedDesc(campaigns) {
  return [...(campaigns || [])].sort((a, b) => {
    const aDefault = a.isDefault ? 1 : 0;
    const bDefault = b.isDefault ? 1 : 0;
    if (aDefault !== bDefault) return bDefault - aDefault;
    const ta = a.updatedAt ? Date.parse(a.updatedAt) : 0;
    const tb = b.updatedAt ? Date.parse(b.updatedAt) : 0;
    return tb - ta;
  });
}

function _checkCampaignConflicts(camp, claim, campaignId, conflicts) {
  if (!camp || String(camp.id) === String(campaignId)) return;
  if (String(camp.status || "").toLowerCase() !== "active") return;
  const assigned = camp.assignedChannels || camp.channels || [];
  for (const ch of assigned) {
    if (claim.has(ch)) {
      conflicts.push({
        channel: ch,
        campaignId: camp.id,
        campaignName: camp.campaignName || camp.name || "Untitled campaign",
      });
    }
  }
}

/**
 * Find other *active* campaigns that already use any of `channels`.
 * Returns [{ channel, campaignId, campaignName }].
 */
export function findActiveChannelConflicts(campaigns, campaignId, channels) {
  const claim = new Set((channels || []).filter(Boolean));
  if (!claim.size) return [];

  const conflicts = [];
  for (const camp of campaigns || []) {
    _checkCampaignConflicts(camp, claim, campaignId, conflicts);
  }
  return conflicts;
}

function _getPeerSources(camp, claim) {
  if (Array.isArray(camp.signalSources) && camp.signalSources.length) {
    return camp.signalSources;
  }
  return (camp.assignedChannels || camp.channels || []).filter((k) => claim.has(k) || k === "rnc" || k === "custom");
}

/**
 * Find other *active* campaigns that already claim any of `signalSources`.
 * Returns [{ signalSource, campaignId, campaignName }].
 */
function _isValidCampaignConflict(camp, campaignId) {
  if (!camp || String(camp.id) === String(campaignId)) return false;
  return String(camp.status || "").toLowerCase() === "active";
}

export function findActiveSignalConflicts(campaigns, campaignId, signalSources) {
  const claim = new Set((signalSources || []).filter(Boolean));
  if (!claim.size) return [];

  const conflicts = [];
  for (const camp of campaigns || []) {
    if (!_isValidCampaignConflict(camp, campaignId)) continue;
    for (const src of _getPeerSources(camp, claim)) {
      if (claim.has(src)) {
        conflicts.push({
          signalSource: src,
          campaignId: camp.id,
          campaignName: camp.campaignName || camp.name || "Untitled campaign",
        });
      }
    }
  }
  return conflicts;
}

/** Human-readable confirm / toast text for channel overlaps. */
export function formatChannelConflictWarning(conflicts) {
  if (!conflicts?.length) return "";
  const byChannel = new Map();
  for (const item of conflicts) {
    const label = CHANNEL_LABELS[item.channel] || item.channel;
    const list = byChannel.get(label) || [];
    if (!list.includes(item.campaignName)) list.push(item.campaignName);
    byChannel.set(label, list);
  }
  const lines = Array.from(byChannel.entries()).map(
    ([ch, names]) => `${ch}: already used by ${names.join(", ")}`,
  );
  return (
    `These signals are already assigned to other active campaigns:\n\n${lines.join("\n")}\n\n`
    + "Continuing may run overlapping workflows on the same signal. "
    + "Only the Default campaign auto-enrolls new RNCs. Continue anyway?"
  );
}

const CHANNEL_LABELS = Object.fromEntries(
  OUTREACH_ASSIGN_CHANNEL_OPTIONS.map((c) => [c.key, c.name]),
);

export function formatRelativeUpdated(iso, fallback = "—") {
  if (!iso) return fallback;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return fallback;
  const diffMs = Date.now() - ts;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
